/**
 * Sheet 12, a rejected run (mission 6 WP07 T042, T043; FR-010, FR-024,
 * FR-026; U.1, U.2, U.4, B.3, D.1, D.2, D.5, A.5, ANA.13; research §1.4,
 * §1.14; post-plan S7, S12, T13; contracts/ui-api.md "Rejected (sheet 12)").
 *
 * Both paths: the header with "Fix and upload again" (the region's one
 * primary), the rejecting stage with the credits consumed from the body
 * (12/1), and the retention statements (12/4): the Class 1/Class 2 note
 * always, the removal date only when `expires_at` is set (D.2).
 *
 * - Path A, the layout validator (12/2 to 12/7, 12/9): the plain statement
 *   (12/3; severities as uppercase words, no badges), the error count, the
 *   column groups that filter the table, the row-level table, "Error report
 *   CSV" through `files.validationReport` only with the audit notice beside it
 *   (FR-026, A.5), and the version-mismatch variant when the upload's errors
 *   hold row 1 `trellis_layout`.
 * - Path B, the structural pre-check (12/10): the structural header, the
 *   count and the `{code, detail, offset}` rows. No upload read, no groups, no
 *   report, no mismatch. The Path A statement names layout validation, which
 *   did not run on Path B, so the structural header and the Path B statement
 *   (owner decision 2026-09-26) stand in for it.
 *
 * No severity badge, chip or count renders here, so the ANA.4 legend is not
 * required (T13).
 *
 * The assistant (mission 8 WP09 T048; FR-012, FR-013, FR-025; SAT.1, SAT.2;
 * 12/7, 12/8; deviation A6; research §1.19, §1.23): the screen renders inside
 * `AssistantDock`. On Path A, beside every `errors_by_column` group, a link
 * `assistantAskAboutColumn` opens the panel with the typed prefill `{kind:
 * 'column', column, count}` (the offer is on every error column, not only
 * code-list columns: A6). Path B has no entry. The triage prompt is hidden
 * (`warnCount` 0: a rejected run has no findings), and after a close focus
 * returns to the page `h1`, since this screen has no rail opener (OR-10).
 */
import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { AssistantDock } from '@/components/trellis/assistant/AssistantDock'
import { AssistantPanel } from '@/components/trellis/assistant/AssistantPanel'
import { useTenant } from '@/components/trellis/AuthGate'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { RunHeader } from '@/components/trellis/RunHeader'
import { StatCard } from '@/components/trellis/StatCard'
import { Button } from '@/components/ui/button'
import { cn } from '@/components/ui/cn'
import { ApiError, type Problem } from '@/lib/api'
import type { PreCheckRejection, Run, ValidationRejection } from '@/lib/api/runs'
import { useAssistantPanel } from '@/lib/assistant-panel'
import { TransferError, validationReport } from '@/lib/files'
import { Mono, count, date, isoDateTime } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { uploadQuery } from '@/lib/queries'
import { ErrorTable } from './rejected/ErrorTable'
import { StructuralErrors } from './rejected/StructuralErrors'
import type { RunScreenProps } from './types'

/** Drawn labels of sheet 12 (callouts 2 and 9). */
const FIX_AND_UPLOAD = 'Fix and upload again'
const ERROR_REPORT = 'Error report CSV'
const DECLARE_V1 = 'Upload again declaring v1'
/** The summary card's label (copy request for WP13 in the WP07 handoff). */
const ERRORS_LABEL = 'Errors'

function fixLink(run: Run): string {
  if (run.path === 'analyze') return '/app/runs/new?path=analyze'
  const layout = run.layout_version === undefined ? '' : `&layout=${encodeURIComponent(run.layout_version)}`
  return `/app/runs/new?path=generate${layout}`
}

function StageLine({ run, stage }: { run: Run; stage: 'stage.validation' | 'stage.preCheck' }) {
  return (
    <p data-rejecting-stage="" className="text-body text-navy-950">
      {/* "N credits" follows the drawn "Cancelled · 0 credits" of sheet 13 callout 2. */}
      {copyText(stage)} · <Mono>{count(run.credits_consumed)}</Mono> credits {copyText('sublabel.consumed')}
    </p>
  )
}

function Retention({ run }: { run: Run }) {
  let line = null
  if (typeof run.expires_at === 'string') {
    const day = date(run.expires_at)
    const [before, ...after] = PROVISIONAL_COPY.retentionLine.render({ expiresAt: day }).split(day)
    line = (
      <p>
        {before}
        <time dateTime={isoDateTime(run.expires_at)}>{day}</time>
        {after.join(day)}
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-1 text-caption text-navy-600">
      <p>{copyText('retentionClassNote')}</p>
      {line}
    </div>
  )
}

type ReportFailure = { kind: 'transfer'; message: string } | { kind: 'problem'; problem: Problem } | { kind: 'generic' }

/** "Error report CSV": `files.validationReport` only; the URL is never rendered or stored (FR-026, S7). */
function ReportAction({ reportUrl, runId }: { reportUrl: string; runId: string }) {
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<ReportFailure | null>(null)
  const inFlight = useRef(false)

  async function download() {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setFailure(null)
    try {
      await validationReport(reportUrl, runId)
    } catch (err) {
      if (err instanceof TransferError) setFailure({ kind: 'transfer', message: err.message })
      else if (err instanceof ApiError) setFailure({ kind: 'problem', problem: err.problem })
      else setFailure({ kind: 'generic' })
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  return (
    <div data-report="" className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <Button variant="secondary" loading={busy} onClick={() => void download()}>
          {ERROR_REPORT}
        </Button>
        <p className="text-caption text-navy-600">{copyText('auditLogNotice')}</p>
      </div>
      {failure?.kind === 'transfer' ? (
        <Band kind="blocked">
          <p>{failure.message}</p>
        </Band>
      ) : null}
      {failure?.kind === 'problem' ? <ProblemBand problem={failure.problem} /> : null}
      {failure?.kind === 'generic' ? (
        <Band kind="blocked">
          <p>{copyText('genericError')}</p>
        </Band>
      ) : null}
    </div>
  )
}

function ErrorCount({ value }: { value: number }) {
  return (
    <div className="w-48">
      <StatCard label={ERRORS_LABEL} value={<Mono data-error-count="">{count(value)}</Mono>} />
    </div>
  )
}

/** The docked panel for this run; the tenant is read only while it is open (FR-012). */
function RunAssistant({ runId }: { runId: string }) {
  const tenant = useTenant()
  return <AssistantPanel runId={runId} tenantName={tenant.data?.legal_name ?? ''} warnCount={0} />
}

function PathA({ run, rejection }: { run: Run; rejection: ValidationRejection }) {
  const [column, setColumn] = useState<string | null>(null)
  const { openPanel } = useAssistantPanel()
  const upload = useQuery(uploadQuery(run.upload_id))
  const groups = Object.entries(rejection.errors_by_column).sort(([a, x], [b, y]) => y - x || (a < b ? -1 : 1))
  const mismatch = upload.data?.errors?.some((e) => e.row === 1 && e.column === 'trellis_layout') ?? false
  const reportUrl = rejection.report_url ?? upload.data?.report_url ?? null

  return (
    <>
      <p className="text-body text-navy-700">{copyText('rejectedStatement')}</p>
      {mismatch ? (
        <Band
          kind="warning"
          action={
            <Button asChild variant="secondary" size="sm">
              <Link to="/app/runs/new?path=generate&layout=v1">{DECLARE_V1}</Link>
            </Button>
          }
        >
          <p>{copyText('versionMismatch')}</p>
        </Band>
      ) : null}
      <div className="flex flex-wrap items-end gap-4">
        <ErrorCount value={rejection.error_count} />
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {groups.map(([name, n]) => {
            const selected = column === name
            return (
              // Docked at 1280 the column is 532 px; a long column name must wrap, never run under the panel (NFR-007).
              <div key={name} data-column-group={name} className="flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-1">
                <Button
                  variant="secondary"
                  size="sm"
                  aria-pressed={selected}
                  className={cn(selected && 'border-navy-950 bg-navy-100')}
                  onClick={() => setColumn(selected ? null : name)}
                >
                  <Mono>{name}</Mono>
                  <Mono className="text-navy-600">{count(n)}</Mono>
                </Button>
                {/* 12/8, deviation A6: the offer on every error column. */}
                <Button
                  variant="link"
                  size="sm"
                  className="whitespace-normal text-left"
                  onClick={() => openPanel({ prefill: { kind: 'column', column: name, count: n } })}
                >
                  {PROVISIONAL_COPY.assistantAskAboutColumn.render({ column: name })}
                </Button>
              </div>
            )
          })}
        </div>
      </div>
      {reportUrl === null ? null : <ReportAction reportUrl={reportUrl} runId={run.run_id} />}
      <ErrorTable uploadId={run.upload_id} column={column} />
    </>
  )
}

function PathB({ rejection }: { rejection: PreCheckRejection }) {
  return (
    <>
      <p className="text-body text-navy-700">{copyText('pathBStructuralHeader')}</p>
      {/* owner decision 2026-09-26 (decision 6): the Path B variant of the statement. */}
      <p className="text-body text-navy-700">{copyText('rejectedStatementPathB')}</p>
      <ErrorCount value={rejection.error_count} />
      <StructuralErrors rejection={rejection} />
    </>
  )
}

export default function Rejected({ run }: RunScreenProps) {
  const rejection = run.rejection
  let body
  if (rejection?.stage === 'validation') {
    body = (
      <>
        <StageLine run={run} stage="stage.validation" />
        <PathA run={run} rejection={rejection} />
      </>
    )
  } else if (rejection?.stage === 'pre-check') {
    body = (
      <>
        <StageLine run={run} stage="stage.preCheck" />
        <PathB rejection={rejection} />
      </>
    )
  } else {
    body = (
      <Band kind="blocked">
        <p>{copyText('genericError')}</p>
      </Band>
    )
  }

  return (
    <AssistantDock runId={run.run_id} panel={<RunAssistant runId={run.run_id} />}>
      <div className="flex flex-col gap-6">
        <RunHeader
          run={run}
          actions={
            <Button asChild>
              <Link to={fixLink(run)}>{FIX_AND_UPLOAD}</Link>
            </Button>
          }
        />
        <p className="text-caption text-navy-600">{copyText('timesInUtc')}</p>
        {body}
        {rejection ? <Retention run={run} /> : null}
      </div>
    </AssistantDock>
  )
}
